import { ref, computed, watch } from 'vue'
import { useLDClient, useLDFlag } from 'launchdarkly-vue-client-sdk'

/**
 * LaunchDarkly composable for feature flag management
 */
export function useLaunchDarkly() {
  
  let client: any = null
  try {
    client = useLDClient()
  } catch (error) {
    console.warn('LaunchDarkly client not available:', error)
  }
  
  const isReady = ref(false)
  const error = ref<string | null>(null)

  // Watch for client ready state
  if (client) {
    client.on('ready', () => {
      isReady.value = true
      console.log('LaunchDarkly client is ready')
    })

    client.on('error', (err: any) => {
      error.value = err.message || 'LaunchDarkly client error'
      console.error('LaunchDarkly error:', err)
    })
  } else {
    error.value = 'LaunchDarkly client not available'
  }

  /**
   * Get a feature flag value with type safety
   */
  function getFlag<T = boolean>(flagKey: string, defaultValue: T): T {
    if (!client) {
      console.warn(`LaunchDarkly not available, returning default value for flag: ${flagKey}`)
      return defaultValue
    }
    
    try {
      const flag = useLDFlag(flagKey, defaultValue)
      return flag.value as T
    } catch (error) {
      console.warn(`Error getting flag ${flagKey}, returning default:`, error)
      return defaultValue
    }
  }

  /**
   * Get a reactive flag that updates when the flag value changes
   */
  function useFlag<T = boolean>(flagKey: string, defaultValue: T) {
    if (!client) {
      console.warn(`LaunchDarkly not available, returning ref with default value for flag: ${flagKey}`)
      return ref(defaultValue)
    }
    
    try {
      return useLDFlag(flagKey, defaultValue)
    } catch (error) {
      console.warn(`Error using flag ${flagKey}, returning ref with default:`, error)
      return ref(defaultValue)
    }
  }

  return {
    client,
    isReady,
    error,
    getFlag,
    useFlag
  }
}

// Common feature flags - add your flags here for better type safety and discoverability
export const FeatureFlags = {
  // Add new feature flags here
} as const

export type FeatureFlagKey = typeof FeatureFlags[keyof typeof FeatureFlags]
